import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Check, X, Loader2, Inbox, User, Mail, MapPin, Euro, Clock, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending_payment: { label: "Attente paiement", tone: "outline" },
  paid_pending_review: { label: "Payée · à valider", tone: "default" },
  payment_failed: { label: "Paiement échoué", tone: "destructive" },
  approved: { label: "Approuvée", tone: "secondary" },
  refused: { label: "Refusée", tone: "destructive" },
  cancelled: { label: "Annulée", tone: "outline" },
};

const STATUS_OPTIONS = ["all", "paid_pending_review", "pending_payment", "approved", "refused", "payment_failed"];

export default function MembershipApplicationsPage() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("paid_pending_review");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refuseDialog, setRefuseDialog] = useState({ open: false, id: null, reason: "" });

  const load = async () => {
    setLoading(true);
    try {
      const url = filter === "all"
        ? `${API}/membership-applications`
        : `${API}/membership-applications?status=${filter}`;
      const { data } = await axios.get(url);
      setApplications(data);
    } catch (e) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (id, allowDuplicate = false) => {
    if (!window.confirm(allowDuplicate
      ? "Approuver malgré une adhésion active existante pour cet email ?"
      : "Approuver cette demande et créer la carte d'adhérent ?")) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/membership-applications/${id}/approve`, {
        allow_duplicate: allowDuplicate,
      });
      const m = data?.membership;
      toast.success(`Adhésion validée · ${m?.public_member_id || m?.member_number || ""}`);
      setSelected(null);
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        const proceed = window.confirm(
          (e.response.data?.detail || "Email déjà adhérent.") +
          "\n\nVoulez-vous quand même créer une adhésion supplémentaire ?"
        );
        if (proceed) return approve(id, true);
        toast.info("Approbation annulée");
      } else {
        toast.error(e.response?.data?.detail || "Erreur validation");
      }
    } finally { setBusy(false); }
  };

  const refuse = async () => {
    if (!refuseDialog.reason.trim()) return toast.error("Motif requis.");
    setBusy(true);
    try {
      await axios.post(`${API}/membership-applications/${refuseDialog.id}/refuse`, {
        reason: refuseDialog.reason.trim(),
      });
      toast.success("Demande refusée · remboursement automatique déclenché si transaction existante.");
      setRefuseDialog({ open: false, id: null, reason: "" });
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur refus");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="membership-applications-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-primary flex items-center gap-3">
          <Inbox className="h-7 w-7" /> Demandes d'adhésion
        </h1>
        <div className="flex gap-2">
          {STATUS_OPTIONS.map(s => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-3 h-9 text-xs rounded-md border transition ${filter === s ? "bg-primary text-white border-primary" : "bg-white text-muted-foreground border-border hover:border-primary/50"}`}
              data-testid={`filter-${s}`}
            >
              {s === "all" ? "Toutes" : STATUS_META[s]?.label || s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : applications.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <Inbox className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Aucune demande pour ce filtre.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {applications.map(a => (
            <Card key={a.id} className="border-border hover:border-primary/40 transition" data-testid={`app-${a.id}`}>
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-heading text-lg font-semibold text-primary truncate">
                      {a.civility} {a.first_name} {a.last_name}
                    </p>
                    <Badge variant={STATUS_META[a.status]?.tone || "outline"}>
                      {STATUS_META[a.status]?.label || a.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{a.email}</span>
                    {a.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{a.city}</span>}
                    {a.payment_amount != null && (
                      <span className="inline-flex items-center gap-1"><Euro className="h-3 w-3" />{a.payment_amount.toFixed(2)} {a.payment_currency}</span>
                    )}
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(a.created_at).toLocaleString("fr-FR")}</span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button variant="outline" onClick={() => setSelected(a)} data-testid={`detail-${a.id}`}>Détail</Button>
                  {a.status === "paid_pending_review" && (
                    <>
                      <Button onClick={() => approve(a.id)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${a.id}`}>
                        <Check className="h-4 w-4 mr-1" />Approuver
                      </Button>
                      <Button variant="outline" onClick={() => setRefuseDialog({ open: true, id: a.id, reason: "" })} disabled={busy} className="text-destructive border-destructive" data-testid={`refuse-${a.id}`}>
                        <X className="h-4 w-4 mr-1" />Refuser
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary flex items-center gap-2">
              <User className="h-5 w-5" /> Détail demande #{selected?.id?.slice(0, 8)}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <Row label="Statut">
                <Badge variant={STATUS_META[selected.status]?.tone || "outline"}>{STATUS_META[selected.status]?.label || selected.status}</Badge>
              </Row>
              <Row label="Identité">{selected.civility} {selected.first_name} {selected.last_name}</Row>
              <Row label="Email">{selected.email}</Row>
              <Row label="Téléphone">{selected.phone || "-"}</Row>
              <Row label="Adresse">{selected.address_line1}, {selected.postal_code} {selected.city}, {selected.country}</Row>
              <Row label="Type">{selected.membership_type} · {selected.validity_type}</Row>
              <Row label="Tarif">{selected.payment_amount ? `${selected.payment_amount.toFixed(2)} ${selected.payment_currency}` : "-"}</Row>
              <Row label="Consentement">
                {selected.immediate_access_consent ? (
                  <span className="inline-flex items-center gap-1 text-emerald-600"><ShieldCheck className="h-3 w-3" />Accepté · v{selected.consent_version} · {selected.consent_datetime ? new Date(selected.consent_datetime).toLocaleString("fr-FR") : "-"}</span>
                ) : "-"}
              </Row>
              <Row label="Transaction">{selected.external_transaction_id || "-"}</Row>
              <Row label="Payé le">{selected.paid_at ? new Date(selected.paid_at).toLocaleString("fr-FR") : "-"}</Row>
              {selected.refused_reason && <Row label="Motif refus">{selected.refused_reason}</Row>}
              {selected.refund_status && <Row label="Remboursement">{selected.refund_status} {selected.refund_id ? `(${selected.refund_id})` : ""}</Row>}
              {selected.status === "paid_pending_review" && (
                <div className="flex gap-2 pt-4 border-t border-border">
                  <Button onClick={() => approve(selected.id)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 flex-1">
                    {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}Approuver et créer la carte
                  </Button>
                  <Button variant="outline" onClick={() => setRefuseDialog({ open: true, id: selected.id, reason: "" })} disabled={busy} className="text-destructive border-destructive flex-1">
                    <X className="h-4 w-4 mr-1" />Refuser et rembourser
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={refuseDialog.open} onOpenChange={(v) => !v && setRefuseDialog({ open: false, id: null, reason: "" })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refuser la demande</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Un remboursement Sandbox sera automatiquement déclenché si la transaction existe.</p>
          <Input
            placeholder="Motif du refus (obligatoire)"
            value={refuseDialog.reason}
            onChange={(e) => setRefuseDialog(r => ({ ...r, reason: e.target.value }))}
            data-testid="refuse-reason-input"
          />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setRefuseDialog({ open: false, id: null, reason: "" })}>Annuler</Button>
            <Button onClick={refuse} disabled={busy || !refuseDialog.reason.trim()} className="bg-red-600 hover:bg-red-700" data-testid="refuse-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <X className="h-4 w-4 mr-1" />}Confirmer le refus
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-32 shrink-0 text-muted-foreground">{label}</span>
      <span className="flex-1 text-primary break-words">{children}</span>
    </div>
  );
}
